import { ReactNode, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import NsContent from 'components/common/ns-content/NsContent';
import { TariffCard } from './TariffCard';
import { useTariffList } from 'components/direct-sales/quote/hooks/useTariffList';
import { NsSecondaryCta } from 'components/common/ns-secondary-cta/NsSecondaryCta';
import { TariffCardsSkeleton } from './TariffCardsSkeleton';
import { pushEvent } from 'state/analytics/analytics-slice';

import type { TProduct } from 'api/models/TProduct';
import {
  selectIsLoadingPost as selectIsLoadingAbandonedCart
} from 'state/abandoned-cart/abandoned-cart-slice';

export const testId = 'tariffCardsTestId';

type CardsWrapperProps = {
  children: ReactNode;
  testId: string;
};

const CardsWrapper = ({ children, testId }: CardsWrapperProps) => (
  <NsContent data-testid={testId} role="list">
    {children}
  </NsContent>
);

type TariffCardsProps = {
  products?: TProduct[];
  isLeftColumn?: boolean;
};

export const TariffCards = ({ products = [] }: TariffCardsProps) => {
  const {
    displayedProducts,
    setShowMore,
    showMore,
    isLoadingTariff,
    multiRowProducts
  } = useTariffList({ products });
  const dispatch = useDispatch();
  const isLoadingAbandonedCart = useSelector(selectIsLoadingAbandonedCart, shallowEqual);
  const hasProducts = products?.length > 0;

  const toggleProducts = () => {
    setShowMore(!showMore);

    dispatch(
      pushEvent({
        category: 'componentAction',
        action: 'click',
        label: 'View all tariffs'
      })
    );
  };

  useEffect(() => {
    setShowMore(multiRowProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiRowProducts]);

  if (isLoadingTariff || isLoadingAbandonedCart) {
    return <TariffCardsSkeleton Wrapper={CardsWrapper} />;
  }

  if (!hasProducts) {
    return null;
  }

  return (
    <>
      <CardsWrapper testId={testId}>
        {displayedProducts.map((product) => (
          <TariffCard product={product} key={product.code} />
        ))}
      </CardsWrapper>
      {showMore && (
        <NsSecondaryCta onClick={toggleProducts}>
          View all {products.length} tariffs
        </NsSecondaryCta>
      )}
    </>
  );
};
